import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { feedbackApi, type FeedbackType, type FeedbackStatus } from '../api/feedback';

// The embedded desktop API has no SMTP configured and these tables aren't wired
// into the offline sync protocol (see CLAUDE.md's "Desktop has no email delivery"
// note, same reasoning as the forgot-password gate) — a POST /feedback there would
// silently vanish: no email, no cloud sync, only visible in that device's own
// local SQLite. Fall back to a plain mailto: instead, same as the pre-existing
// "Other ways to reach us" link, so desktop users still reach someone.
const IS_DESKTOP_APP = import.meta.env.VITE_USE_HASH_ROUTER === 'true';

const TYPES = [
  { value: 'feature', label: '✨ Feature Request' },
  { value: 'bug',     label: '🐛 Bug Report' },
  { value: 'other',   label: '💬 General Feedback' },
] as const;

const CATEGORIES = [
  'Invoicing', 'Purchases', 'Payments & Expenses', 'Products & Inventory',
  'Manufacturing', 'Reports & GST', 'Bank Reconciliation', 'E-Invoicing / E-Way Bill',
  'Desktop Sync', 'Login & Account', 'Other',
];

const STATUS_LABEL: Record<FeedbackStatus, string> = {
  open: 'Open', in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed',
};
const STATUS_CLS: Record<FeedbackStatus, string> = {
  open: 'badge-blue', in_progress: 'badge-unpaid', resolved: 'badge-paid', closed: 'badge-gray',
};

export default function FeedbackPage() {
  const [type, setType] = useState<FeedbackType>('feature');
  const [category, setCategory] = useState('');
  const [message, setMessage] = useState('');
  const [lastRequestNumber, setLastRequestNumber] = useState<string | null>(null);
  const [desktopSent, setDesktopSent] = useState(false);
  const qc = useQueryClient();

  const { data: myRequests } = useQuery({
    queryKey: ['feedback', 'mine'],
    queryFn: feedbackApi.listMine,
    staleTime: 30_000,
    enabled: !IS_DESKTOP_APP,
  });

  const submit = useMutation({
    mutationFn: () => feedbackApi.create({ type, category: type === 'bug' ? category || undefined : undefined, message }),
    onSuccess: (result) => {
      setLastRequestNumber(result.requestNumber);
      setMessage('');
      setCategory('');
      qc.invalidateQueries({ queryKey: ['feedback', 'mine'] });
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (IS_DESKTOP_APP) {
      const subject = encodeURIComponent(`Feedback Request [${type}]${category ? ` — ${category}` : ''}`);
      const body = encodeURIComponent(message);
      window.location.href = `mailto:feedback@coreledger.in?subject=${subject}&body=${body}`;
      setDesktopSent(true);
      setMessage('');
      setCategory('');
      return;
    }
    submit.mutate();
  }

  return (
    <div className="page" style={{ maxWidth: 640 }}>
      <div className="page-header">
        <h1 className="page-title">Feedback Request</h1>
      </div>

      <div className="card" style={{ marginBottom: '1rem' }}>
        <div className="card-body">
          <p style={{ fontSize: '0.875rem', color: 'var(--text-2)', marginBottom: '1.25rem', lineHeight: 1.6 }}>
            Have a suggestion, found a bug, or just want to say hello? We read every message.
          </p>

          {IS_DESKTOP_APP && desktopSent && (
            <div className="success-box">
              ✅ Thanks! Your email client should have opened. If not, email us at <strong>feedback@coreledger.in</strong>.
            </div>
          )}
          {!IS_DESKTOP_APP && submit.isSuccess && lastRequestNumber && (
            <div className="success-box">
              ✅ Thanks! Your feedback request <strong>{lastRequestNumber}</strong> has been submitted — we'll be in touch.
            </div>
          )}
          {!IS_DESKTOP_APP && submit.isError && (
            <div className="error-box">
              Couldn't submit that — please try again, or email us directly at <strong>feedback@coreledger.in</strong>.
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '1rem' }}>
              <label style={{ marginBottom: '0.5rem', display: 'block' }}>Feedback Type</label>
              <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap' }}>
                {TYPES.map(t => (
                  <button
                    key={t.value} type="button"
                    onClick={() => setType(t.value)}
                    style={{
                      padding: '0.5rem 1rem', borderRadius: 8, cursor: 'pointer',
                      border: `2px solid ${type === t.value ? 'var(--primary)' : 'var(--border)'}`,
                      background: type === t.value ? 'var(--primary-light)' : 'var(--surface)',
                      color: type === t.value ? 'var(--primary)' : 'var(--text-2)',
                      fontWeight: type === t.value ? 700 : 500, fontSize: '0.875rem',
                    }}
                  >{t.label}</button>
                ))}
              </div>
            </div>

            {type === 'bug' && (
              <div style={{ marginBottom: '1rem' }}>
                <label>
                  Category
                  <select value={category} onChange={e => setCategory(e.target.value)} style={{ marginTop: '0.3rem' }}>
                    <option value="">Select where you're seeing the issue…</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
            )}

            <div style={{ marginBottom: '1.25rem' }}>
              <label>
                Your Message
                <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  rows={6}
                  placeholder="Describe your feedback in detail — the more context, the better!"
                  required
                  style={{ marginTop: '0.3rem', resize: 'vertical' }}
                />
              </label>
            </div>

            <button type="submit" className="btn btn-primary" disabled={!message.trim() || submit.isPending}>
              {submit.isPending ? 'Submitting…' : IS_DESKTOP_APP ? '📧 Open Email to Send' : '📧 Submit Feedback Request'}
            </button>
          </form>
        </div>
      </div>

      {!IS_DESKTOP_APP && myRequests && myRequests.length > 0 && (
        <div className="card" style={{ marginBottom: '1rem' }}>
          <div className="card-body">
            <div style={{ fontWeight: 600, marginBottom: '0.75rem', color: 'var(--text)' }}>My Feedback Requests</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
              {myRequests.map(r => (
                <div key={r.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.75rem', padding: '0.6rem 0', borderBottom: '1px solid var(--border)', fontSize: '0.85rem' }}>
                  <div>
                    <div style={{ fontWeight: 600 }}>
                      {r.requestNumber}
                      {r.category && <span style={{ color: 'var(--text-2)', fontWeight: 400 }}> · {r.category}</span>}
                    </div>
                    <div style={{ color: 'var(--text-2)', fontSize: '0.8rem' }}>
                      {new Date(r.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                  <span className={`badge ${STATUS_CLS[r.status]}`}>{STATUS_LABEL[r.status]}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="card">
        <div className="card-body" style={{ fontSize: '0.875rem', color: 'var(--text-2)' }}>
          <div style={{ fontWeight: 600, marginBottom: '0.5rem', color: 'var(--text)' }}>Other ways to reach us</div>
          <div style={{ lineHeight: 2 }}>
            📧 Email: <a href="mailto:feedback@coreledger.in" style={{ color: 'var(--primary)' }}>feedback@coreledger.in</a>
          </div>
        </div>
      </div>
    </div>
  );
}
